// Lote V-11.A.2 — Admin Facturación tab con 5 sub-tabs.
//
// Sub-tabs:
//   • emisor        — formulario IssuerSetting (datos fiscais)
//   • numeracion    — series + contadores
//   • templates     — email + PDF (V-11.B vai facer preview real)
//   • estado        — activación + stats
//   • contabilidade — táboa facturas + filtros + exports

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Hash,
  Mail,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Save,
  Settings as SettingsIcon,
  ShieldCheck,
} from 'lucide-react';

import Spinner from '../../components/ui/Spinner';
import { describeAxiosError } from '../../api/errors';
import { useToast } from '../../context/ToastContext';
import {
  activateIssuer,
  deactivateIssuer,
  downloadInvoicePdf,
  downloadInvoicesCsv,
  downloadInvoicesXlsx,
  downloadInvoicesZip,
  getActivationCheck,
  getInvoiceStats,
  getIssuerSettings,
  listInvoices,
  resendInvoiceEmail,
  updateIssuerSettings,
  voidInvoice,
  type InvoiceListItem,
  type IssuerSetting,
  type IssuerSettingUpdate,
} from '../../api/admin-invoicing';

type SubtabKey = 'emisor' | 'numeracion' | 'templates' | 'estado' | 'contabilidade';

const SUBTAB_LABELS: Record<SubtabKey, string> = {
  emisor: 'Emisor',
  numeracion: 'Numeración',
  templates: 'Templates',
  estado: 'Estado',
  contabilidade: 'Contabilidade',
};

const SUBTAB_ORDER: SubtabKey[] = ['emisor', 'numeracion', 'templates', 'estado', 'contabilidade'];

const SUBTAB_ICONS: Record<SubtabKey, JSX.Element> = {
  emisor: <SettingsIcon size={16} />,
  numeracion: <Hash size={16} />,
  templates: <Mail size={16} />,
  estado: <ShieldCheck size={16} />,
  contabilidade: <FileText size={16} />,
};

export default function InvoicingTab(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const rawSubtab = params.get('subtab');
  const subtab: SubtabKey = (SUBTAB_ORDER as string[]).includes(rawSubtab ?? '')
    ? (rawSubtab as SubtabKey)
    : 'emisor';

  const [issuer, setIssuer] = useState<IssuerSetting | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getIssuerSettings();
      setIssuer(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function switchSubtab(next: SubtabKey): void {
    const newParams = new URLSearchParams(params);
    newParams.set('tab', 'invoicing');
    newParams.set('subtab', next);
    setParams(newParams, { replace: true });
  }

  return (
    <div className="space-y-6">
      {issuer && !issuer.is_active && (
        <div className="border border-amber-300 bg-amber-50 rounded-2xl p-4 text-sm text-amber-900 flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 flex-none mt-0.5" />
          <div>
            <p className="font-semibold">Modo borrador activo</p>
            <p className="text-xs mt-1 leading-relaxed">
              As facturas xeraranse e gardaranse pero <strong>NON</strong> se enviarán por email.
              Vai a <strong>Estado</strong> para activar a produción cando teñas todos os datos.
            </p>
          </div>
        </div>
      )}

      <nav className="border-b border-border flex gap-1 overflow-x-auto" role="tablist">
        {SUBTAB_ORDER.map((key) => {
          const active = key === subtab;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => switchSubtab(key)}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition flex items-center gap-2 whitespace-nowrap ${
                active ? 'border-rauxa text-rauxa' : 'border-transparent text-muted hover:text-dark'
              }`}
            >
              {SUBTAB_ICONS[key]}
              {SUBTAB_LABELS[key]}
            </button>
          );
        })}
      </nav>

      <section role="tabpanel">
        {loading || !issuer ? (
          <div className="flex items-center gap-2 text-muted">
            <Spinner size={16} /> Cargando…
          </div>
        ) : (
          <>
            {subtab === 'emisor' && <EmisorSubtab issuer={issuer} onSaved={refresh} />}
            {subtab === 'numeracion' && <NumeracionSubtab issuer={issuer} onSaved={refresh} />}
            {subtab === 'templates' && <TemplatesSubtab issuer={issuer} onSaved={refresh} />}
            {subtab === 'estado' && <EstadoSubtab issuer={issuer} onChanged={refresh} />}
            {subtab === 'contabilidade' && <ContabilidadeSubtab />}
          </>
        )}
      </section>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-tab 1 — Emisor (datos fiscais)
// ───────────────────────────────────────────────────────────────────────────

function EmisorSubtab({
  issuer,
  onSaved,
}: {
  issuer: IssuerSetting;
  onSaved: () => void;
}): JSX.Element {
  const { addToast } = useToast();
  const [form, setForm] = useState<IssuerSettingUpdate>({});
  const [saving, setSaving] = useState(false);

  function update<K extends keyof IssuerSettingUpdate>(
    field: K,
    value: IssuerSettingUpdate[K],
  ): void {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function val<K extends keyof IssuerSetting>(field: K): string {
    if (field in form && (form as Record<string, unknown>)[field as string] !== undefined) {
      return String((form as Record<string, unknown>)[field as string] ?? '');
    }
    const v = issuer[field];
    return v == null ? '' : String(v);
  }

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await updateIssuerSettings(form);
      setForm({});
      addToast('Datos do emisor gardados', 'success');
      onSaved();
    } catch (err) {
      addToast(describeAxiosError(err, 'Erro ao gardar'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
      <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 text-xs text-amber-900">
        ⚠️ Os cambios <strong>só aplican a facturas futuras</strong>. As facturas xa emitidas
        conservan os datos do momento da emisión (snapshot).
      </div>

      <Fieldset legend="Identidade fiscal">
        <Field label="Razón social *" hint="Ex.: «LeyroChain SL», «Manuel Castro»">
          <input
            type="text"
            value={val('legal_name')}
            onChange={(e) => update('legal_name', e.target.value)}
            className={inputClass}
            maxLength={200}
          />
        </Field>
        <Field label="NIF / CIF *" hint="Formato: 12345678X, B12345678, X1234567A">
          <input
            type="text"
            value={val('nif')}
            onChange={(e) => update('nif', e.target.value.toUpperCase())}
            className={inputClass}
            maxLength={12}
          />
        </Field>
        <Field label="Forma xurídica" hint="Ex.: Sociedade Limitada, Autónomo">
          <input
            type="text"
            value={val('legal_form')}
            onChange={(e) => update('legal_form', e.target.value)}
            className={inputClass}
            maxLength={100}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Domicilio fiscal">
        <Field label="Liña 1 *">
          <input
            type="text"
            value={val('address_line1')}
            onChange={(e) => update('address_line1', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Liña 2">
          <input
            type="text"
            value={val('address_line2')}
            onChange={(e) => update('address_line2', e.target.value)}
            className={inputClass}
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Cidade *">
            <input
              type="text"
              value={val('address_city')}
              onChange={(e) => update('address_city', e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Provincia *">
            <input
              type="text"
              value={val('address_province')}
              onChange={(e) => update('address_province', e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="CP *">
            <input
              type="text"
              value={val('address_postal_code')}
              onChange={(e) => update('address_postal_code', e.target.value)}
              className={inputClass}
              maxLength={5}
            />
          </Field>
        </div>
        <Field label="País">
          <input
            type="text"
            value={val('address_country')}
            onChange={(e) => update('address_country', e.target.value)}
            className={inputClass}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Contacto">
        <Field label="Email facturación *">
          <input
            type="email"
            value={val('email')}
            onChange={(e) => update('email', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Teléfono">
          <input
            type="tel"
            value={val('phone')}
            onChange={(e) => update('phone', e.target.value)}
            className={inputClass}
            maxLength={30}
          />
        </Field>
        <Field label="Web">
          <input
            type="url"
            value={val('website')}
            onChange={(e) => update('website', e.target.value)}
            className={inputClass}
            placeholder="https://chain.leyro.es"
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Rexistro mercantil (só sociedades)">
        <Field label="Texto rexistro">
          <textarea
            value={val('registry_data')}
            onChange={(e) => update('registry_data', e.target.value)}
            className={`${inputClass} min-h-[80px]`}
            rows={3}
            placeholder="Inscrita no Rexistro Mercantil de Pontevedra, Tomo X, Folio Y…"
            maxLength={1000}
          />
        </Field>
      </Fieldset>

      <div className="text-xs text-muted">
        Última actualización: {new Date(issuer.updated_at).toLocaleString('gl-ES')}
      </div>

      <div className="flex items-center gap-3 sticky bottom-0 bg-white py-3 border-t border-border">
        <button
          type="submit"
          disabled={saving || Object.keys(form).length === 0}
          className="inline-flex items-center gap-2 bg-rauxa text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-rauxa/90 disabled:opacity-50"
        >
          {saving ? <Spinner size={14} /> : <Save size={16} />}
          Gardar cambios
        </button>
        {Object.keys(form).length > 0 && (
          <button
            type="button"
            onClick={() => setForm({})}
            className="text-sm text-muted hover:text-dark"
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-tab 2 — Numeración
// ───────────────────────────────────────────────────────────────────────────

function NumeracionSubtab({
  issuer,
  onSaved,
}: {
  issuer: IssuerSetting;
  onSaved: () => void;
}): JSX.Element {
  const { addToast } = useToast();
  const [form, setForm] = useState<IssuerSettingUpdate>({});
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await updateIssuerSettings(form);
      setForm({});
      addToast('Numeración gardada', 'success');
      onSaved();
    } catch (err) {
      addToast(describeAxiosError(err, 'Erro ao gardar'), 'error');
    } finally {
      setSaving(false);
    }
  }

  function val<
    K extends
      | 'invoice_series_prefix'
      | 'rectificative_series_prefix'
      | 'invoice_padding'
      | 'reset_annually',
  >(field: K): IssuerSetting[K] {
    if (field in form && form[field] !== undefined) return form[field] as IssuerSetting[K];
    return issuer[field];
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-2xl">
      <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 text-xs text-amber-900">
        ⚠️ A numeración debe ser correlativa sen ocos. Cambialos pode crear inconsistencias coa
        AEAT. Tocar só con coidado.
      </div>

      <Fieldset legend="Serie standard">
        <Field label="Prefixo serie">
          <input
            type="text"
            value={String(val('invoice_series_prefix'))}
            onChange={(e) => setForm((p) => ({ ...p, invoice_series_prefix: e.target.value }))}
            className={inputClass}
            maxLength={20}
          />
          <p className="text-xs text-muted mt-1">
            Exemplo de número xerado: <code>{val('invoice_series_prefix')}2026-000247</code>
          </p>
        </Field>
      </Fieldset>

      <Fieldset legend="Serie rectificativas">
        <Field label="Prefixo serie">
          <input
            type="text"
            value={String(val('rectificative_series_prefix'))}
            onChange={(e) =>
              setForm((p) => ({ ...p, rectificative_series_prefix: e.target.value }))
            }
            className={inputClass}
            maxLength={20}
          />
        </Field>
      </Fieldset>

      <Fieldset legend="Formato número">
        <Field label="Padding (díxitos do número)">
          <input
            type="number"
            min={1}
            max={12}
            value={Number(val('invoice_padding'))}
            onChange={(e) =>
              setForm((p) => ({ ...p, invoice_padding: parseInt(e.target.value, 10) || 6 }))
            }
            className={inputClass}
          />
        </Field>
        <Field label="">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(val('reset_annually'))}
              onChange={(e) => setForm((p) => ({ ...p, reset_annually: e.target.checked }))}
              className="w-4 h-4"
            />
            Resetear contador cada 1 xaneiro automaticamente
          </label>
        </Field>
      </Fieldset>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving || Object.keys(form).length === 0}
          className="inline-flex items-center gap-2 bg-rauxa text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-rauxa/90 disabled:opacity-50"
        >
          {saving ? <Spinner size={14} /> : <Save size={16} />}
          Gardar cambios
        </button>
      </div>
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-tab 3 — Templates
// ───────────────────────────────────────────────────────────────────────────

function TemplatesSubtab({
  issuer,
  onSaved,
}: {
  issuer: IssuerSetting;
  onSaved: () => void;
}): JSX.Element {
  const { addToast } = useToast();
  const [form, setForm] = useState<IssuerSettingUpdate>({});
  const [saving, setSaving] = useState(false);

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    try {
      await updateIssuerSettings(form);
      setForm({});
      addToast('Templates gardados', 'success');
      onSaved();
    } catch (err) {
      addToast(describeAxiosError(err, 'Erro ao gardar'), 'error');
    } finally {
      setSaving(false);
    }
  }

  function valStr(field: keyof IssuerSettingUpdate): string {
    if (field in form && (form as Record<string, unknown>)[field as string] !== undefined) {
      return String((form as Record<string, unknown>)[field as string] ?? '');
    }
    const v = issuer[field as keyof IssuerSetting];
    return v == null ? '' : String(v);
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
      <Fieldset legend="Email">
        <Field label="De (From) *">
          <input
            type="text"
            value={valStr('email_from')}
            onChange={(e) => setForm((p) => ({ ...p, email_from: e.target.value }))}
            className={inputClass}
            placeholder="LeyroChain Fact. <noreply@leyro.es>"
          />
        </Field>
        <Field label="Reply-to">
          <input
            type="email"
            value={valStr('email_reply_to')}
            onChange={(e) => setForm((p) => ({ ...p, email_reply_to: e.target.value }))}
            className={inputClass}
          />
        </Field>
        <Field label="Subject" hint="Variables: {{number}}, {{recipient_name}}">
          <input
            type="text"
            value={valStr('email_subject_template')}
            onChange={(e) => setForm((p) => ({ ...p, email_subject_template: e.target.value }))}
            className={inputClass}
            maxLength={200}
          />
        </Field>
        <Field
          label="Body (HTML)"
          hint="Variables: {{number}}, {{recipient_name}}, {{recipient_nif}}, {{total_eur}}, {{portal_link}}, {{issuer_name}}, {{operation_date}}"
        >
          <textarea
            value={valStr('email_body_template')}
            onChange={(e) => setForm((p) => ({ ...p, email_body_template: e.target.value }))}
            className={`${inputClass} font-mono text-xs min-h-[200px]`}
            rows={10}
            placeholder="<p>Estimado/a {{recipient_name}}…"
          />
        </Field>
      </Fieldset>

      <Fieldset legend="PDF">
        <Field label="Logo URL (S3)">
          <input
            type="text"
            value={valStr('pdf_logo_url')}
            onChange={(e) => setForm((p) => ({ ...p, pdf_logo_url: e.target.value }))}
            className={inputClass}
            placeholder="https://..."
          />
          <p className="text-xs text-muted mt-1">En V-11.B engadirase upload directo desde aquí.</p>
        </Field>
        <Field label="Footer documento">
          <textarea
            value={valStr('pdf_footer_text')}
            onChange={(e) => setForm((p) => ({ ...p, pdf_footer_text: e.target.value }))}
            className={`${inputClass} min-h-[60px]`}
            rows={2}
            maxLength={1000}
          />
        </Field>
        <Field label="Color primario">
          <input
            type="color"
            value={valStr('pdf_primary_color') || '#E83D18'}
            onChange={(e) => setForm((p) => ({ ...p, pdf_primary_color: e.target.value }))}
            className="h-10 w-20 border border-border rounded cursor-pointer"
          />
        </Field>
      </Fieldset>

      <div className="border border-blue-200 bg-blue-50 rounded-xl p-3 text-xs text-blue-900">
        ℹ️ Vista previa do PDF + email engadirase en V-11.B cando se implemente o motor de xeración
        PDF.
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving || Object.keys(form).length === 0}
          className="inline-flex items-center gap-2 bg-rauxa text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-rauxa/90 disabled:opacity-50"
        >
          {saving ? <Spinner size={14} /> : <Save size={16} />}
          Gardar templates
        </button>
      </div>
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-tab 4 — Estado (activación)
// ───────────────────────────────────────────────────────────────────────────

function EstadoSubtab({
  issuer,
  onChanged,
}: {
  issuer: IssuerSetting;
  onChanged: () => void;
}): JSX.Element {
  const { addToast } = useToast();
  const [check, setCheck] = useState<Awaited<ReturnType<typeof getActivationCheck>> | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    void getActivationCheck()
      .then(setCheck)
      .catch(() => undefined);
  }, [issuer.updated_at]);

  async function handleActivate(): Promise<void> {
    if (!window.confirm('Vas activar a emisión real de facturas. Confirmas?')) return;
    setWorking(true);
    try {
      await activateIssuer();
      addToast('Modo produción activado', 'success');
      onChanged();
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido activar'), 'error');
    } finally {
      setWorking(false);
    }
  }

  async function handleDeactivate(): Promise<void> {
    if (!window.confirm('Vas volver ao modo borrador. Confirmas?')) return;
    setWorking(true);
    try {
      await deactivateIssuer();
      addToast('Volto a modo borrador', 'success');
      onChanged();
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido desactivar'), 'error');
    } finally {
      setWorking(false);
    }
  }

  const requiredLabels: Record<string, string> = {
    legal_name: 'Razón social',
    nif: 'NIF / CIF',
    address_line1: 'Liña 1 do enderezo',
    address_city: 'Cidade',
    address_province: 'Provincia',
    address_postal_code: 'Código postal',
    email: 'Email facturación',
    email_from: 'Email From (Templates)',
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="border border-border rounded-2xl p-6 bg-white">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center flex-none ${
              issuer.is_active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            {issuer.is_active ? <CheckCircle2 size={26} /> : <PauseCircle size={26} />}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-dark">
              {issuer.is_active ? 'Produción activa' : 'Modo borrador'}
            </h3>
            <p className="text-sm text-muted mt-1 leading-relaxed">
              {issuer.is_active
                ? 'As facturas envíanse automaticamente aos destinatarios cando se asina o contrato.'
                : 'As facturas xéranse + gárdanse pero non se envían por email. Útil para probas e configuración inicial.'}
            </p>
            {issuer.activated_at && (
              <p className="text-xs text-muted mt-2">
                Activado: {new Date(issuer.activated_at).toLocaleString('gl-ES')}
              </p>
            )}
          </div>
        </div>

        {check && !issuer.is_active && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-sm font-semibold text-dark mb-3">Para activar produción precisas:</p>
            <ul className="space-y-1.5 text-sm">
              {Object.entries(requiredLabels).map(([key, label]) => {
                const missing = check.missing_fields.includes(key);
                return (
                  <li key={key} className="flex items-center gap-2">
                    {missing ? (
                      <span className="w-4 h-4 rounded-full border-2 border-amber-500" />
                    ) : (
                      <CheckCircle2 size={16} className="text-green-600" />
                    )}
                    <span className={missing ? 'text-muted' : 'text-dark'}>{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          {issuer.is_active ? (
            <button
              type="button"
              onClick={handleDeactivate}
              disabled={working}
              className="inline-flex items-center gap-2 bg-amber-600 text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
            >
              {working ? <Spinner size={14} /> : <PauseCircle size={16} />}
              Volver a borrador
            </button>
          ) : (
            <button
              type="button"
              onClick={handleActivate}
              disabled={working || !check?.ready}
              className="inline-flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {working ? <Spinner size={14} /> : <PlayCircle size={16} />}
              Activar produción
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-tab 5 — Contabilidade
// ───────────────────────────────────────────────────────────────────────────

function ContabilidadeSubtab(): JSX.Element {
  const { addToast } = useToast();
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getInvoiceStats>> | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    from: string;
    to: string;
    type: '' | 'STANDARD' | 'RECTIFICATIVE';
    recipient_role: '' | 'SELLER' | 'BUYER';
    email_status: '' | 'PENDING' | 'SENT' | 'BOUNCED' | 'FAILED' | 'SKIPPED';
    search: string;
  }>({
    from: '',
    to: '',
    type: '',
    recipient_role: '',
    email_status: '',
    search: '',
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        getInvoiceStats(),
        listInvoices({
          from: filters.from ? new Date(filters.from).toISOString() : undefined,
          to: filters.to ? new Date(filters.to).toISOString() : undefined,
          type: filters.type || undefined,
          recipient_role: filters.recipient_role || undefined,
          email_status: filters.email_status || undefined,
          search: filters.search || undefined,
          page,
          page_size: 50,
        }),
      ]);
      setStats(s);
      setInvoices(list.items);
      setTotal(list.total);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function quickRange(range: 'month' | 'quarter' | 'year'): void {
    const now = new Date();
    let from: Date;
    if (range === 'month') {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (range === 'quarter') {
      const q = Math.floor(now.getMonth() / 3);
      from = new Date(now.getFullYear(), q * 3, 1);
    } else {
      from = new Date(now.getFullYear(), 0, 1);
    }
    setFilters((f) => ({
      ...f,
      from: from.toISOString().slice(0, 10),
      to: new Date().toISOString().slice(0, 10),
    }));
    setPage(1);
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard
            title="Mes actual"
            count={stats.current_month.count}
            total={stats.current_month.total_cents}
            vat={stats.current_month.vat_cents}
            highlight
          />
          <StatCard
            title={`Q${stats.current_quarter.quarter} ${stats.current_quarter.year}`}
            count={stats.current_quarter.count}
            total={stats.current_quarter.total_cents}
            vat={stats.current_quarter.vat_cents}
          />
          <StatCard
            title={`Ano ${stats.current_fiscal_year.year}`}
            count={stats.current_fiscal_year.count}
            total={stats.current_fiscal_year.total_cents}
            vat={stats.current_fiscal_year.vat_cents}
          />
        </div>
      )}

      {/* Filtros */}
      <div className="border border-border rounded-2xl p-4 bg-white space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <Field label="Dende">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => {
                setFilters((f) => ({ ...f, from: e.target.value }));
                setPage(1);
              }}
              className={inputClass}
            />
          </Field>
          <Field label="Ata">
            <input
              type="date"
              value={filters.to}
              onChange={(e) => {
                setFilters((f) => ({ ...f, to: e.target.value }));
                setPage(1);
              }}
              className={inputClass}
            />
          </Field>
          <Field label="Tipo">
            <select
              value={filters.type}
              onChange={(e) => {
                setFilters((f) => ({ ...f, type: e.target.value as typeof filters.type }));
                setPage(1);
              }}
              className={inputClass}
            >
              <option value="">Todos</option>
              <option value="STANDARD">Standard</option>
              <option value="RECTIFICATIVE">Rectificativa</option>
            </select>
          </Field>
          <Field label="Rol">
            <select
              value={filters.recipient_role}
              onChange={(e) => {
                setFilters((f) => ({
                  ...f,
                  recipient_role: e.target.value as typeof filters.recipient_role,
                }));
                setPage(1);
              }}
              className={inputClass}
            >
              <option value="">Todos</option>
              <option value="SELLER">Vendedor</option>
              <option value="BUYER">Comprador</option>
            </select>
          </Field>
          <Field label="Email">
            <select
              value={filters.email_status}
              onChange={(e) => {
                setFilters((f) => ({
                  ...f,
                  email_status: e.target.value as typeof filters.email_status,
                }));
                setPage(1);
              }}
              className={inputClass}
            >
              <option value="">Todos</option>
              <option value="PENDING">Pendente</option>
              <option value="SENT">Enviada</option>
              <option value="BOUNCED">Bounced</option>
              <option value="FAILED">Erro</option>
              <option value="SKIPPED">Skipped</option>
            </select>
          </Field>
          <Field label="Busca">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilters((f) => ({ ...f, search: e.target.value }));
                setPage(1);
              }}
              placeholder="NIF, nome, nº…"
              className={inputClass}
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">Rápido:</span>
          <button type="button" onClick={() => quickRange('month')} className={pillClass}>
            Mes
          </button>
          <button type="button" onClick={() => quickRange('quarter')} className={pillClass}>
            Trimestre
          </button>
          <button type="button" onClick={() => quickRange('year')} className={pillClass}>
            Ano
          </button>
          <button type="button" onClick={() => refresh()} className={pillClass}>
            <RefreshCw size={12} className="inline mr-1" /> Refrescar
          </button>
        </div>
      </div>

      {/* Exports */}
      <div className="border border-border rounded-2xl p-4 bg-white">
        <p className="text-sm font-semibold text-dark mb-2">Exports</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              downloadInvoicesCsv({
                from: filters.from ? new Date(filters.from).toISOString() : undefined,
                to: filters.to ? new Date(filters.to).toISOString() : undefined,
                type: filters.type || undefined,
              })
            }
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-border hover:bg-subtle text-dark"
          >
            📊 CSV modelo 303
          </button>
          <button
            type="button"
            onClick={() =>
              downloadInvoicesXlsx({
                from: filters.from ? new Date(filters.from).toISOString() : undefined,
                to: filters.to ? new Date(filters.to).toISOString() : undefined,
                type: filters.type || undefined,
              })
            }
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-border hover:bg-subtle text-dark"
          >
            📑 XLSX xestoría
          </button>
          <button
            type="button"
            onClick={() =>
              downloadInvoicesZip({
                from: filters.from ? new Date(filters.from).toISOString() : undefined,
                to: filters.to ? new Date(filters.to).toISOString() : undefined,
                type: filters.type || undefined,
              })
            }
            className="text-xs px-3 py-1.5 rounded-full bg-white border border-border hover:bg-subtle text-dark"
          >
            📦 ZIP facturas PDF
          </button>
        </div>
        <p className="text-xs text-muted mt-2">
          • <strong>CSV</strong>: data, número, tipo, destinatario+NIF, base, %IVE, cuota e total. •{' '}
          <strong>XLSX</strong>: 3 follas (detalle + resumo trimestral + modelo 347). •{' '}
          <strong>ZIP</strong>: PDFs orixinais cun INDEX.csv para arquivo legal.
        </p>
      </div>

      {/* Lista facturas */}
      <div className="border border-border rounded-2xl bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="text-sm font-semibold text-dark">
            Facturas ({total} {total === 1 ? 'resultado' : 'resultados'})
          </p>
          {loading && <Spinner size={14} />}
        </div>
        {invoices.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted">
            <FileText size={32} className="mx-auto mb-2 text-muted" />
            {loading ? 'Cargando…' : 'Aínda non se emitiron facturas.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {invoices.map((inv) => (
              <li key={inv.id} className="px-4 py-3 hover:bg-subtle/30">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-dark">
                        {inv.full_number}
                      </span>
                      <span
                        className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                          inv.invoice_type === 'RECTIFICATIVE'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {inv.invoice_type === 'RECTIFICATIVE' ? 'Rect.' : 'Std'}
                      </span>
                      <span
                        className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                          inv.recipient_role === 'SELLER'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-teal-100 text-teal-800'
                        }`}
                      >
                        {inv.recipient_role === 'SELLER' ? 'Vendedor' : 'Comprador'}
                      </span>
                    </div>
                    <p className="text-sm text-muted mt-0.5 truncate">
                      {inv.recipient_name}
                      {inv.recipient_nif ? ` · ${inv.recipient_nif}` : ''} ·{' '}
                      {new Date(inv.issued_at).toLocaleDateString('gl-ES')}
                    </p>
                  </div>
                  <div className="text-right flex-none">
                    <p className="text-sm font-bold text-dark">{formatCents(inv.total_cents)} €</p>
                    <p className="text-xs text-muted">
                      Base {formatCents(inv.base_cents)} + IVE {formatCents(inv.vat_cents)}
                    </p>
                    <div className="mt-1">
                      <EmailStatusBadge status={inv.email_status} />
                    </div>
                    <div className="mt-2 flex justify-end gap-2">
                      {inv.invoice_type === 'STANDARD' && !inv.voided_at && (
                        <button
                          type="button"
                          disabled={actingId === inv.id}
                          onClick={async () => {
                            const reason = window.prompt('Motivo da anulación?');
                            if (!reason || reason.trim().length < 3) return;
                            setActingId(inv.id);
                            try {
                              const res = await voidInvoice(inv.id, reason.trim());
                              addToast(
                                `Rectificativa ${res.rectificative_number} emitida`,
                                'success',
                              );
                              void refresh();
                            } catch (err) {
                              addToast(describeAxiosError(err, 'Erro ao anular'), 'error');
                            } finally {
                              setActingId(null);
                            }
                          }}
                          className="text-[10px] uppercase font-semibold px-2 py-1 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                          title="Emite factura rectificativa que anula esta"
                        >
                          Anular
                        </button>
                      )}
                      {/* Phase 27 (B-09 fix) — descarga directa do PDF
                          da factura. Backend stream + audit log. */}
                      <button
                        type="button"
                        disabled={actingId === inv.id}
                        onClick={async () => {
                          setActingId(inv.id);
                          try {
                            await downloadInvoicePdf(inv.id, inv.full_number);
                          } catch (err) {
                            addToast(describeAxiosError(err, 'Erro ao descargar PDF'), 'error');
                          } finally {
                            setActingId(null);
                          }
                        }}
                        className="text-[10px] uppercase font-semibold px-2 py-1 rounded-full border border-rauxa text-rauxa hover:bg-rauxa/5 disabled:opacity-50"
                        title="Descargar PDF da factura"
                      >
                        📄 PDF
                      </button>
                      {(inv.email_status === 'BOUNCED' ||
                        inv.email_status === 'FAILED' ||
                        inv.email_status === 'SENT') && (
                        <button
                          type="button"
                          disabled={actingId === inv.id}
                          onClick={async () => {
                            setActingId(inv.id);
                            try {
                              await resendInvoiceEmail(inv.id);
                              addToast(
                                'Reenvío encolado — cron enviará no seguinte tick',
                                'success',
                              );
                              void refresh();
                            } catch (err) {
                              addToast(describeAxiosError(err, 'Erro ao reenviar'), 'error');
                            } finally {
                              setActingId(null);
                            }
                          }}
                          className="text-[10px] uppercase font-semibold px-2 py-1 rounded-full border border-border text-dark hover:bg-subtle disabled:opacity-50"
                          title="Reseta email_status a PENDING para que o cron reenvíe"
                        >
                          Reenviar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        {total > 50 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="text-rauxa font-semibold disabled:text-muted"
            >
              ← Anterior
            </button>
            <span className="text-muted">
              Páxina {page} de {Math.ceil(total / 50)}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil(total / 50)}
              className="text-rauxa font-semibold disabled:text-muted"
            >
              Seguinte →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Compoñentes auxiliares
// ───────────────────────────────────────────────────────────────────────────

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rauxa/30 focus:border-rauxa';
const pillClass = 'text-xs px-3 py-1.5 rounded-full border border-border hover:bg-subtle text-dark';

function Fieldset({
  legend,
  children,
}: {
  legend: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <fieldset className="border border-border rounded-2xl p-4 space-y-3">
      <legend className="px-2 text-sm font-semibold text-dark">{legend}</legend>
      {children}
    </fieldset>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      {label && <label className="block text-xs font-medium text-muted mb-1">{label}</label>}
      {children}
      {hint && <p className="text-[11px] text-muted mt-1">{hint}</p>}
    </div>
  );
}

function StatCard({
  title,
  count,
  total,
  vat,
  highlight = false,
}: {
  title: string;
  count: number;
  total: number;
  vat: number;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-2xl p-4 ${
        highlight ? 'bg-rauxa/5 border-2 border-rauxa/20' : 'bg-white border border-border'
      }`}
    >
      <p className="text-xs uppercase tracking-wide font-medium text-muted">{title}</p>
      <p className="text-2xl font-bold text-dark mt-1">{count}</p>
      <p className="text-xs text-muted">facturas</p>
      <div className="mt-3 pt-3 border-t border-border/50 space-y-0.5">
        <p className="text-xs text-muted">
          Total: <strong className="text-dark">{formatCents(total)} €</strong>
        </p>
        <p className="text-xs text-muted">
          IVE repercutido: <strong className="text-amber-700">{formatCents(vat)} €</strong>
        </p>
      </div>
    </div>
  );
}

function EmailStatusBadge({
  status,
}: {
  status: 'PENDING' | 'SENT' | 'BOUNCED' | 'FAILED' | 'SKIPPED';
}): JSX.Element {
  const map: Record<typeof status, { label: string; cls: string }> = {
    PENDING: { label: '⏳ pendente', cls: 'text-amber-700' },
    SENT: { label: '✓ enviada', cls: 'text-green-700' },
    BOUNCED: { label: '✗ bounce', cls: 'text-red-700' },
    FAILED: { label: '⚠ erro', cls: 'text-red-700' },
    SKIPPED: { label: '— borrador', cls: 'text-muted' },
  };
  const m = map[status];
  return <span className={`text-[11px] font-medium ${m.cls}`}>{m.label}</span>;
}

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}
